"use client";

import { Stepper } from "./stock-movement-stepper";
import { InputLookup } from "@/features/inventory/components/inputs/InputLookup";
import { useBatchesByInput } from "@/features/inventory/hooks/useBatches";
import { useNotification } from "@/shared/hooks/useNotification";
import { useState } from "react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { CreateStockMovementRequest, MovementType } from "@/features/inventory/services/stockMovementsApi";
import { useRecordMovement } from "../../hooks/useStockMovement";
import type { Input as InventoryInput } from "@/features/inventory/services/inputsApi";
import type { Batch } from "@/features/inventory/services/batchesApi";

interface FormData {
    quantity: string;
    movementDate: string;
    referenceNumber: string;
    greenhouseId: string;
    farmId: string;
    fieldId: string;
    productionBatchNumber: string;
    reason: string;
    notes: string;
}

export function StockMovementStepper() {
    const notify = useNotification();
    const [selectedInput, setSelectedInput] = useState<InventoryInput | null>(null);
    const [selectedInputId, setSelectedInputId] = useState("");
    const [selectedBatchId, setSelectedBatchId] = useState("");
    const [movementType, setMovementType] = useState<MovementType | "">(
        ""
    );

    const { mutate: recordMovement, isPending } = useRecordMovement();

    const [formData, setFormData] = useState<FormData>({
        quantity: "",
        movementDate: new Date().toISOString().split("T")[0],
        referenceNumber: "",
        greenhouseId: "",
        farmId: "",
        fieldId: "",
        productionBatchNumber: "",
        reason: "",
        notes: "",
    });

    const { data: batches = [] } = useBatchesByInput(selectedInputId);

    const handleInputChange = (input: InventoryInput) => {
        setSelectedInputId(input.id);
        setSelectedInput(input);
        setSelectedBatchId("");
    };

    const handleFormChange = (field: keyof FormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const selectedBatch = batches.find((b) => b.id === selectedBatchId);

    const handleBatchChange = (batchId: string) => {
        setSelectedBatchId(batchId);
        const batch = batches.find((candidate) => candidate.id === batchId);
        setFormData((previous) => ({
            ...previous,
            productionBatchNumber: batch?.batchNumber ?? "",
        }));
    };

    const isDetailsFormValid = () => {
        return (
            formData.quantity &&
            formData.referenceNumber &&
            formData.productionBatchNumber &&
            formData.reason &&
            parseInt(formData.quantity) > 0
        );
    };

    const handleSubmit = () => {
        if (!isDetailsFormValid()) {
            notify.warning("Please fill in all required fields");
            return;
        }
        const payload: CreateStockMovementRequest = {
            inputId: selectedInputId,
            inputBatchId: selectedBatchId,
            movementType: movementType as MovementType,
            quantity: parseInt(formData.quantity),
            movementDate: formData.movementDate,
            referenceNumber: formData.referenceNumber,
            greenhouseId: formData.greenhouseId || undefined,
            farmId: formData.farmId || undefined,
            fieldId: formData.fieldId || undefined,
            productionBatchNumber: formData.productionBatchNumber,
            reason: formData.reason,
            notes: formData.notes,
            requiresApproval: true,
        };

        recordMovement(
            {
                ...payload,
                type: movementType.toLowerCase() as Lowercase<MovementType>,
            },
            {
                onSuccess: () => notify.success("Stock movement submitted for approval"),
                onError: () => notify.error("Failed to submit stock movement"),
            }
        );
    };

    return (
        <Stepper.Root
            className="w-full space-y-8"
            orientation="horizontal"
        >
            {({ stepper }) => (
                <>
                    {/* Stepper List */}
                    <Stepper.List className="flex list-none gap-2 flex-row
                                    items-center justify-between">
                        {stepper.state.all.map((stepData, index) => {
                            const currentIndex = stepper.state.current.index;
                            const status =
                                index < currentIndex
                                    ? "success"
                                    : index === currentIndex
                                        ? "active"
                                        : "inactive";
                            return (
                                <Stepper.Item
                                    key={stepData.id}
                                    step={stepData.id}
                                    className="group peer relative flex shrink-0
                  items-center gap-2"
                                >
                                    <Stepper.Trigger
                                        render={(domProps) => (
                                            <Button
                                                className="rounded-full"
                                                variant={
                                                    status === "inactive"
                                                        ? "secondary"
                                                        : "default"
                                                }
                                                size="icon"
                                                {...domProps}
                                            >
                                                {stepData.icon && <stepData.icon className="h-4
                        w-4" />}
                                            </Button>
                                        )}
                                    />
                                    <div className="flex flex-col items-start gap-1">
                                        <Stepper.Title
                                            render={(domProps) => (
                                                <h4
                                                    className="text-base font-medium"
                                                    {...domProps}
                                                >
                                                    {stepData.title}
                                                </h4>
                                            )}
                                        />
                                        <Stepper.Description
                                            render={(domProps) => (
                                                <p
                                                    className="text-sm text-muted-foreground"
                                                    {...domProps}
                                                >
                                                    {stepData.description}
                                                </p>
                                            )}
                                        />
                                    </div>
                                </Stepper.Item>
                            );
                        })}
                    </Stepper.List>

                    {/* Step Content */}
                    <div className="space-y-4">
                        {stepper.state.current.data.id === "selection" && (
                            <SelectionStep
                                batches={batches}
                                selectedInputId={selectedInputId}
                                selectedBatchId={selectedBatchId}
                                selectedInput={selectedInput ?? undefined}
                                onInputChange={handleInputChange}
                                onBatchChange={handleBatchChange}
                            />
                        )}

                        {stepper.state.current.data.id === "movementType" && (
                            <MovementTypeStep
                                movementType={movementType}
                                onChange={setMovementType}
                            />
                        )}

                        {stepper.state.current.data.id === "details" && (
                            <DetailsStep
                                formData={formData}
                                onChange={handleFormChange}
                                movementType={movementType}
                            />
                        )}

                        {stepper.state.current.data.id === "review" && (
                            <ReviewStep
                                selectedInput={selectedInput ?? undefined}
                                selectedBatch={selectedBatch}
                                movementType={movementType}
                                formData={formData}
                            />
                        )}

                        {/* {stepper.state.current.data.id === "success" && (
                            <SuccessStep />
                        )} */}
                    </div>

                    {/* Navigation */}
                    <div className="flex justify-between">
                        {!stepper.state.isFirst && (
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => stepper.navigation.prev()}
                            >
                                Previous
                            </Button>
                        )}

                        <Button
                            type="button"
                            className="ml-auto"
                            onClick={() =>
                                stepper.state.current.data.id === "review"
                                    ? handleSubmit()
                                    : stepper.navigation.next()
                            }
                            disabled={
                                isPending ||
                                (stepper.state.current.data.id === "selection" &&
                                    (!selectedInputId || !selectedBatchId)) ||
                                (stepper.state.current.data.id === "movementType" &&
                                    !movementType) ||
                                (stepper.state.current.data.id === "review" &&
                                    !isDetailsFormValid())
                            }
                        >
                            {stepper.state.current.data.id === "review"
                                ? isPending ? "Submitting..." : "Submit"
                                : "Continue"}
                        </Button>
                    </div>
                </>
            )}
        </Stepper.Root>
    );
}

const SelectionStep = ({
    batches,
    selectedInputId,
    selectedBatchId,
    selectedInput,
    onInputChange,
    onBatchChange,
}: {
    batches: Batch[];
    selectedInputId: string;
    selectedBatchId: string;
    selectedInput?: InventoryInput | null;
    onInputChange: (input: InventoryInput) => void;
    onBatchChange: (batchId: string) => void;
}) => (
    <div className="space-y-4 rounded border p-8">
        <h3 className="font-medium">Select Input & Batch</h3>

        {/* Input Autocomplete */}
        <InputLookup value={selectedInputId} selectedInput={selectedInput} onSelect={onInputChange} />

        {/* Show selected input */}
        {selectedInput && (
            <p className="text-sm text-muted-foreground">
                Selected: {selectedInput.inputName}
            </p>
        )}

        {/* Batch Select */}
        <Select
            value={selectedBatchId}
            onValueChange={onBatchChange}
            disabled={!selectedInputId}
        >
            <SelectTrigger>
                <SelectValue
                    placeholder={
                        selectedInputId
                            ? "Select a batch"
                            : "Select an input first"
                    }
                />
            </SelectTrigger>
            <SelectContent>
                {batches.map((batch) => (
                    <SelectItem key={batch.id} value={batch.id}>
                        {batch.batchNumber}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    </div>
);

const MovementTypeStep = ({
    movementType,
    onChange,
}: {
    movementType: MovementType | "";
    onChange: (movementType: MovementType) => void;
}) => (
    <div className="space-y-4 rounded border bg-secondary p-8">
        <h3 className="font-medium">Select Movement Type</h3>
        <div className="space-y-3">
            {(["Receipt", "Issue", "Return", "Adjustment"] as MovementType[]).map((type) => (
                <label
                    key={type}
                    className="flex items-center gap-3 cursor-pointer"
                >
                    <input
                        type="radio"
                        name="movementType"
                        value={type}
                        checked={movementType === type}
                        onChange={(e) => onChange(e.target.value as MovementType)}
                        className="w-4 h-4"
                    />
                    <span className="text-sm">{type}</span>
                </label>
            ))}
        </div>
    </div>
);

const DetailsStep = ({
    formData,
    onChange,
    movementType,
}: {
    formData: FormData;
    onChange: (field: keyof FormData, value: string) => void;
    movementType: MovementType | "";
}) => {
    const date = formData.movementDate
        ? new Date(formData.movementDate)
        : undefined;
    return (
        <div className="space-y-4 rounded border bg-secondary p-8">
            <h3 className="font-medium">Movement Details</h3>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
                {/* Quantity */}
                <div className="space-y-2">
                    <label className="text-sm font-medium">Quantity *</label>
                    <input
                        type="number"
                        placeholder="Enter quantity"
                        value={formData.quantity}
                        onChange={(e) => onChange("quantity", e.target.value)}
                        className="w-full px-3 py-2 rounded border bg-background"
                    />
                </div>

                {/* Movement Date */}
                <div className="space-y-2">
                    <label className="text-sm font-medium">Movement Date *</label>
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                className="w-full justify-start text-left font-normal"
                            >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {date ? format(date, "PPP") : "Pick a date"}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                                mode="single"
                                selected={date}
                                onSelect={(selectedDate) => {
                                    if (selectedDate) {
                                        onChange(
                                            "movementDate",
                                            selectedDate.toISOString().split("T")[0]
                                        );
                                    }
                                }}
                            // disabled={(date) =>
                            //     date > new Date() || date < new Date("2000-01-01")
                            // }
                            />
                        </PopoverContent>
                    </Popover>
                </div>
                {/* Reference Number */}
                <div className="space-y-2">
                    <label className="text-sm font-medium">
                        Reference Number *
                    </label>
                    <input
                        type="text"
                        placeholder="e.g., PO-2024-001"
                        value={formData.referenceNumber}
                        onChange={(e) => onChange("referenceNumber", e.target.value)}
                        className="w-full px-3 py-2 rounded border bg-background"
                    />
                </div>

                {/* Production Batch Number */}
                <div className="space-y-2">
                    <label className="text-sm font-medium">
                        Production Batch Number *
                    </label>
                    <input
                        type="text"
                        placeholder="e.g., BATCH-2024-001"
                        value={formData.productionBatchNumber}
                        onChange={(e) =>
                            onChange("productionBatchNumber", e.target.value)
                        }
                        className="w-full px-3 py-2 rounded border bg-background"
                    />
                </div>
            </div>

            {/* Location Fields - Conditional based on movement type */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {(movementType === "Receipt" ||
                    movementType === "Adjustment") && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Greenhouse</label>
                            <input
                                type="text"
                                placeholder="Greenhouse ID or name"
                                value={formData.greenhouseId}
                                onChange={(e) => onChange("greenhouseId", e.target.value)}
                                className="w-full px-3 py-2 rounded border bg-background"
                            />
                        </div>
                    )}

                {(movementType === "Issue" ||
                    movementType === "Adjustment") && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Field</label>
                            <input
                                type="text"
                                placeholder="Field ID or name"
                                value={formData.fieldId}
                                onChange={(e) => onChange("fieldId", e.target.value)}
                                className="w-full px-3 py-2 rounded border bg-background"
                            />
                        </div>
                    )}

                {(movementType === "Return" ||
                    movementType === "Adjustment") && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Farm</label>
                            <input
                                type="text"
                                placeholder="Farm ID or name"
                                value={formData.farmId}
                                onChange={(e) => onChange("farmId", e.target.value)}
                                className="w-full px-3 py-2 rounded border bg-background"
                            />
                        </div>
                    )}
            </div>

            {/* Reason */}
            <div className="space-y-2">
                <label className="text-sm font-medium">Reason *</label>
                <textarea
                    placeholder="Enter the reason for this movement"
                    value={formData.reason}
                    onChange={(e) => onChange("reason", e.target.value)}
                    className="w-full px-3 py-2 rounded border bg-background
          min-h-[100px] resize-none"
                />
            </div>

            {/* Notes */}
            <div className="space-y-2">
                <label className="text-sm font-medium">Notes</label>
                <textarea
                    placeholder="Add any additional notes (optional)"
                    value={formData.notes}
                    onChange={(e) => onChange("notes", e.target.value)}
                    className="w-full px-3 py-2 rounded border bg-background
          min-h-[80px] resize-none"
                />
            </div>
        </div>
    );
};

const ReviewStep = ({
    selectedInput,
    selectedBatch,
    movementType,
    formData,
}: {
    selectedInput?: InventoryInput;
    selectedBatch?: Batch;
    movementType: MovementType | "";
    formData: FormData;
}) => {
    const getReasonSuggestions = (type: MovementType) => {
        const suggestions: Record<MovementType, string> = {
            'Receipt': '✓ Stock received and verified',
            'Issue': '✓ Stock issued for use',
            'Return': '✓ Stock returned to inventory',
            'Adjustment': '✓ Inventory adjustment recorded',
        };
        return suggestions[type] || '';
    };

    const getLocationFields = (type: MovementType) => {
        const locationFields: Record<
            MovementType,
            { label: string; value: string } | Array<{ label: string; value: string }>
        > = {
            'Receipt': { label: 'Greenhouse', value: formData.greenhouseId },
            'Issue': { label: 'Field', value: formData.fieldId },
            'Return': { label: 'Farm', value: formData.farmId },
            'Adjustment': [
                { label: 'Greenhouse', value: formData.greenhouseId },
                { label: 'Field', value: formData.fieldId },
                { label: 'Farm', value: formData.farmId },
            ],
        };
        return locationFields[type];
    };

    if (!movementType) return null;

    const locationField = getLocationFields(movementType as MovementType);

    return (
        <div className="space-y-6 rounded border bg-secondary p-8">
            <div>
                <h3 className="text-lg font-semibold mb-4">Review Movement</h3>
                <p className="text-sm text-muted-foreground">
                    Please review all details before submitting
                </p>
            </div>

            {/* Input & Batch Summary */}
            <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Selection</h4>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
                    <div>
                        <p className="text-xs text-muted-foreground">Input</p>
                        <p className="text-sm font-medium">{selectedInput?.inputName}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Batch</p>
                        <p className="text-sm font-medium">
                            {selectedBatch?.batchNumber}
                        </p>
                    </div>
                </div>
            </div>

            {/* Movement Type */}
            <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Movement Type</h4>
                <div className="flex items-center gap-2">
                    <div className="px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
                        <p className="text-sm font-medium text-primary">
                            {movementType}
                        </p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        {getReasonSuggestions(movementType as MovementType)}
                    </p>
                </div>
            </div>

            {/* Movement Details */}
            <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Movement Details</h4>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
                    <div>
                        <p className="text-xs text-muted-foreground">Quantity</p>
                        <p className="text-sm font-medium">{formData.quantity}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Movement Date</p>
                        <p className="text-sm font-medium">
                            {new Date(formData.movementDate).toLocaleDateString()}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">
                            Reference Number
                        </p>
                        <p className="text-sm font-medium">
                            {formData.referenceNumber}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">
                            Production Batch Number
                        </p>
                        <p className="text-sm font-medium">
                            {formData.productionBatchNumber}
                        </p>
                    </div>
                </div>
            </div>

            {/* Location Fields - Contextual */}
            <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Location</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array.isArray(locationField) ? (
                        locationField.map((field) => (
                            <div key={field.label}>
                                <p className="text-xs text-muted-foreground">
                                    {field.label}
                                </p>
                                <p className="text-sm font-medium">
                                    {field.value || '—'}
                                </p>
                            </div>
                        ))
                    ) : (
                        <div>
                            <p className="text-xs text-muted-foreground">
                                {locationField.label}
                            </p>
                            <p className="text-sm font-medium">
                                {locationField.value || '—'}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Reason */}
            <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Reason</h4>
                <div className="p-3 rounded bg-muted">
                    <p className="text-sm whitespace-pre-wrap">
                        {formData.reason}
                    </p>
                </div>
            </div>

            {/* Notes */}
            {formData.notes && (
                <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">Notes</h4>
                    <div className="p-3 rounded bg-muted">
                        <p className="text-sm whitespace-pre-wrap">
                            {formData.notes}
                        </p>
                    </div>
                </div>
            )}

            {/* Submission Info */}
            <div className="border-t border-yellow-200 bg-yellow-50
      dark:bg-yellow-900/20 p-4 rounded">
                <p className="text-xs text-yellow-800 dark:text-yellow-200">
                    ⓘ This movement will be submitted for approval and recorded in
                    the system.
                </p>
            </div>
        </div>
    );
};
